export const calculationsArticles = [
    {
        id: 'features.calculations',
        title: 'BOQ Calculation Routines',
        category: 'Features',
        order: 2,
        summary: 'Understanding the 17 calculation routines used in BOQ pricing',
        content: `
Costed.app uses calculation routines to determine how quantities and prices are combined to calculate line item values. Each unit of measure is assigned a calculation routine that defines its behavior.

**Basic Formula:**

For most routines, the calculation is:
\`\`\`
Value = (Quantity / Divisor) × Unit Price
\`\`\`

With optional rounding applied to the adjusted quantity.

**Key Concepts:**

- **Divisor** - Converts quantities to pricing units (e.g., 1000 to convert mm to m)
- **Rounding** - Rounds adjusted quantity to nearest multiple (0 or 1 = no rounding)
- **Running Total** - Some routines calculate percentages of cumulative totals
    `,
        expandableSections: [
            {
                title: 'Standard Calculations (Routine 0-1)',
                content: `
**Routine 0 & 1: Standard Calculation**

Formula: \`Value = Round(Quantity / Divisor) × Unit Price\`

- Divides quantity by the unit's divisor
- Applies rounding if rounding > 1
- Multiplies by unit price

**Example:**
- Quantity: 5500 (mm)
- Divisor: 1000 (convert to metres)
- Rounding: 1 (no rounding)
- Unit Price: $25.00/m
- Result: (5500 / 1000) × $25.00 = **$137.50**

**With Rounding:**
- Quantity: 5500
- Divisor: 1000
- Rounding: 10 (round to nearest 10)
- Unit Price: $25.00
- Adjusted Qty: Round(5.5 to nearest 10) = 10
- Result: 10 × $25.00 = **$250.00**
        `,
            },
            {
                title: 'Linear/Area Calculations (Routine 2-3)',
                content: `
**Routine 2 & 3: Direct Multiplication**

Formula: \`Value = Quantity × Unit Price\`

Used for items where quantity is already in the correct unit (no conversion needed).

**Example:**
- Quantity: 25 (m²)
- Unit Price: $45.00/m²
- Result: 25 × $45.00 = **$1,125.00**
        `,
            },
            {
                title: 'Price Only (Routine 6)',
                content: `
**Routine 6: Price Only**

Formula: \`Value = Unit Price\`

Ignores quantity entirely. Used for lump sum items where the price is fixed regardless of quantity.

**Example:**
- Quantity: 1 (ignored)
- Unit Price: $5,000.00
- Result: **$5,000.00**
        `,
            },
            {
                title: 'Percentage Calculations (Routine 7, 15, 16)',
                content: `
**Routine 7: Percentage of Running Total**

Formula: \`Value = Running Total × (Quantity / 100)\`

Calculates a percentage of all preceding items.

**Example:**
- Running Total: $50,000.00
- Quantity: 10 (represents 10%)
- Result: $50,000.00 × 0.10 = **$5,000.00**

---

**Routine 15: Percentage of Contract**

Formula: \`Value = (Contract Price + GST) × (Quantity / 100)\`

Calculates a percentage of the total contract value including GST.

---

**Routine 16: Percentage of Bill Total**

Formula: \`Value = BOQ Total × (Quantity / 100)\`

Calculates a percentage of the entire bill of quantities total.
        `,
            },
            {
                title: 'Headings and Subtotals (Routine 8, 9, 14)',
                content: `
**Routine 8, 9, 14: Heading Items**

These routines are used for section headings and subtotals:

- **No calculation** - Value is always $0.00
- **Resets running total** - For percentage calculations that follow
- **Display only** - Shows as a heading row in the BOQ

Use these routines to structure your BOQ into logical sections.
        `,
            },
            {
                title: 'Rounding Rules',
                content: `
**Important:** The rounding value behaves as follows:

| Rounding Value | Behavior |
|----------------|----------|
| 0 | No rounding |
| 1 | No rounding (same as 0) |
| > 1 | Round to nearest multiple |

**Examples:**
- Rounding = 5: Round to nearest 5 (3.2 → 5, 7.8 → 10)
- Rounding = 10: Round to nearest 10 (23 → 20, 27 → 30)
- Rounding = 100: Round to nearest 100 (550 → 600, 449 → 400)

This matches the legacy Windows application behavior.
        `,
            },
            {
                title: 'Complete Routine Reference',
                content: `
| Routine | Name | Formula |
|---------|------|---------|
| 0 | Standard | (Qty / Div) × Price |
| 1 | Standard (alt) | (Qty / Div) × Price |
| 2 | Linear | Qty × Price |
| 3 | Area | Qty × Price |
| 4 | Reserved | - |
| 5 | Reserved | - |
| 6 | Price Only | Price |
| 7 | % of Running Total | RunningTotal × (Qty / 100) |
| 8 | Heading | No calc, resets total |
| 9 | Subheading | No calc, resets total |
| 10-13 | Reserved | - |
| 14 | Section Header | No calc, resets total |
| 15 | % of Contract | (Contract + GST) × (Qty / 100) |
| 16 | % of Bill Total | BOQTotal × (Qty / 100) |
        `,
            },
        ],
        keywords: ['calculation', 'routine', 'formula', 'pricing', 'divisor', 'rounding', 'percentage'],
        relatedArticles: ['boq.overview', 'boq.add-item', 'catalogue.units'],
    },
];
